import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { cn } from '@goaltree/ui/lib/utils'
import { useNavigate } from '@tanstack/react-router'
import { useConvexAuth, useMutation, useQuery } from 'convex/react'
import {
  BarChart3,
  CheckCircle2,
  ClipboardList,
  Compass,
  CornerDownLeft,
  Flame,
  Inbox,
  type LucideIcon,
  MessageCircle,
  Mic,
  Plus,
  RefreshCw,
  Search,
  Settings,
  Target,
  Trophy,
  WifiOff,
} from 'lucide-react'
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCoachPersona } from '@/components/ask-coach'
import { habitIconComponent } from '@/components/habit-icon'
import { useIsMobile } from '@/hooks/use-mobile'
import { useOnlineStatus } from '@/hooks/use-online-status'
import { CATEGORY_COLORS, useCategoryFill } from '@/lib/category-color'
import { categoryIcon } from '@/lib/category-icon'
import { useIsCoaching } from '@/lib/coaching-mode'
import { readSnapshot, writeSnapshot } from '@/lib/offline-cache'
import { periodLabel } from '@/lib/schedule'
import { matchScore } from '@/lib/search-match'
import { getRecentIds, pushRecent } from '@/lib/search-recent'
import {
  type SearchTemplate,
  pickSource,
  searchTemplates,
  toSearchTemplate,
} from '@/lib/search-sources'
import { playCreatedSound } from '@/lib/sounds'
import { handleUsageLimit } from '@/lib/usage-limit'
import { goalUrl } from '@/lib/utils'
import { openCapture } from '@/store/capture.store'

// Personal pages, personal capture and the AI coach: left out in coaching mode.
const PERSONAL_ENTRIES = new Set([
  'page-today',
  'page-inbox',
  'page-stats',
  'new-task',
  'new-habit',
  'brain-dump',
  'ask-coach',
  'review',
  'add-inbox',
  'add-habit',
  'ask',
])

type SearchResult = {
  _id: Id<'nodes'>
  title: string
  type: 'goal' | 'milestone' | 'target' | 'task' | 'event'
  category: string
  completedAt: number | null
  parentGoalId: Id<'nodes'> | null
  parentGoalTitle: string
  parentMilestoneId: Id<'nodes'> | null
  parentMilestoneTitle?: string
  isInbox?: boolean
  isHabit?: boolean
  description?: string
  tags?: string[]
  recursPeriod?: string
  recursDays?: number[]
  dueDate?: number
  startsAt?: number
}

type Group = 'goals' | 'plan' | 'events' | 'tasks' | 'habits' | 'inbox'
const GROUP_ORDER: Group[] = ['goals', 'plan', 'events', 'tasks', 'habits', 'inbox']
type Filter = 'all' | 'goals' | 'tasks' | 'habits' | 'inbox' | 'templates'
const NODES_CACHE_KEY = 'search-nodes'
const templatesCacheKey = (locale: string) => `search-templates:${locale}`

function groupOf(r: SearchResult): Group {
  if (r.type === 'goal') return 'goals'
  if (r.type === 'event') return 'events'
  if (r.type === 'milestone' || r.type === 'target') return 'plan'
  if (r.isHabit) return 'habits'
  if (r.isInbox) return 'inbox'
  return 'tasks'
}

function highlight(text: string, query: string): ReactNode {
  const q = query.trim()
  if (!q || q.startsWith('#')) return text
  const idx = text.toLowerCase().indexOf(q.toLowerCase())
  if (idx === -1) return text
  return (
    <>
      {text.slice(0, idx)}
      <mark className="rounded-[3px] bg-primary/25 text-foreground">
        {text.slice(idx, idx + q.length)}
      </mark>
      {text.slice(idx + q.length)}
    </>
  )
}

type Entry = {
  key: string
  section: string
  run: () => void
  render: (active: boolean) => ReactNode
}

// ⌘K palette: search, but also a launcher. Empty → Recent / Jump to / Actions;
// typing → grouped results, then "Add to Inbox" / "Ask <coach>" so it never
// dead-ends.
export function SearchModal() {
  const { t, i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<Filter>('all')
  const [activeIdx, setActiveIdx] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const fill = useCategoryFill()
  const createQuickTask = useMutation(api.nodes.createQuickTask)
  const { isAuthenticated } = useConvexAuth()
  const { name: coachName } = useCoachPersona(isAuthenticated && open)
  const coaching = useIsCoaching()
  const [recentIds, setRecentIds] = useState<string[]>([])

  const online = useOnlineStatus()
  const locale = i18n.language.split('-')[0]
  const q = query.trim()

  const liveNodes = useQuery(api.nodes.getAllForSearch, isAuthenticated && open ? {} : 'skip') as
    | SearchResult[]
    | undefined
  const [nodesSnap, setNodesSnap] = useState<SearchResult[] | null>(null)
  useEffect(() => {
    if (!open) return
    setNodesSnap(readSnapshot<SearchResult[]>(NODES_CACHE_KEY))
  }, [open])
  useEffect(() => {
    if (liveNodes) writeSnapshot(NODES_CACHE_KEY, liveNodes)
  }, [liveNodes])
  const nodeSource = pickSource(liveNodes, nodesSnap)
  const allNodes = nodeSource ?? []
  const noCache = !nodeSource && !online

  // Templates load lazily, only once the user types.
  const liveTemplates = useQuery(
    api.templates.list,
    isAuthenticated && open && q ? { locale } : 'skip',
  )
  const [tplSnap, setTplSnap] = useState<SearchTemplate[] | null>(null)
  useEffect(() => {
    if (open && q) setTplSnap((v) => v ?? readSnapshot<SearchTemplate[]>(templatesCacheKey(locale)))
  }, [open, q, locale])
  const liveTplSlim = useMemo(() => liveTemplates?.map(toSearchTemplate), [liveTemplates])
  useEffect(() => {
    if (liveTplSlim) writeSnapshot(templatesCacheKey(locale), liveTplSlim)
  }, [liveTplSlim, locale])
  const templateHits = useMemo(() => {
    if (!q || (filter !== 'all' && filter !== 'templates')) return []
    return searchTemplates(pickSource(liveTplSlim, tplSnap) ?? [], q)
  }, [q, filter, liveTplSlim, tplSnap])

  const results = useMemo(() => {
    if (!q) return []
    return allNodes
      .map((n) => ({ n, score: matchScore(n, q) }))
      .filter(
        ({ n, score }) =>
          score > 0 &&
          filter !== 'templates' &&
          (filter === 'all' ||
            groupOf(n) === filter ||
            (filter === 'tasks' && (groupOf(n) === 'plan' || groupOf(n) === 'events'))),
      )
      .sort((a, b) => b.score - a.score || Number(!!a.n.completedAt) - Number(!!b.n.completedAt))
      .slice(0, 40)
      .map(({ n }) => n)
  }, [allNodes, q, filter])

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setOpen((v) => !v)
      }
    }
    const onOpenEvent = () => setOpen(true)
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('open-search', onOpenEvent)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('open-search', onOpenEvent)
    }
  }, [])

  useEffect(() => {
    if (!open) return
    setQuery('')
    setFilter('all')
    setActiveIdx(0)
    setRecentIds(getRecentIds())
    requestAnimationFrame(() => inputRef.current?.focus())
  }, [open])

  useEffect(() => setActiveIdx(0), [q, filter])
  useEffect(() => setTplSnap(null), [locale])

  function close() {
    setOpen(false)
  }

  function openResult(r: SearchResult) {
    close()
    pushRecent(r._id)
    if (r.type === 'event') {
      navigate({ to: '/everyday', search: { tab: 'events', focusEvent: r._id } })
    } else if (r.isInbox) {
      navigate({
        to: '/everyday',
        search:
          r.type === 'task'
            ? { focusTask: r._id, tab: r.isHabit ? ('habits' as const) : ('tasks' as const) }
            : {},
      })
    } else if (r.type === 'goal') {
      navigate({
        to: '/goals/$goalId',
        params: { goalId: goalUrl(r.title, r._id).replace('/goals/', '') },
      })
    } else if (r.parentGoalId) {
      navigate({
        to: '/goals/$goalId',
        params: { goalId: goalUrl(r.parentGoalTitle, r.parentGoalId).replace('/goals/', '') },
        search: r.parentMilestoneId
          ? { openMilestone: r.parentMilestoneId, focusNode: r._id }
          : { focusNode: r._id },
      })
    }
  }

  async function addToInbox(kind: 'task' | 'habit') {
    if (!q) return
    close()
    try {
      const id = await createQuickTask({ title: q, ...(kind === 'habit' ? { isHabit: true } : {}) })
      playCreatedSound()
      navigate({
        to: '/everyday',
        search: {
          focusTask: id as unknown as string,
          tab: kind === 'habit' ? ('habits' as const) : ('tasks' as const),
        },
      })
    } catch (err) {
      handleUsageLimit(err)
    }
  }

  function askCoach(message?: string) {
    close()
    window.dispatchEvent(new CustomEvent('open-coach', message ? { detail: { message } } : {}))
  }

  const rowCls = (active: boolean) =>
    cn(
      'flex w-full cursor-pointer items-center gap-3 rounded-xl px-2.5 text-left transition-colors',
      isMobile ? 'py-3' : 'py-2',
      active ? 'bg-muted/70' : 'hover:bg-muted/40',
    )
  const enterHint = (active: boolean) => (
    <CornerDownLeft
      className={cn(
        'size-3.5 shrink-0 text-muted-foreground',
        active ? 'opacity-100' : 'opacity-0',
      )}
    />
  )
  const iconTile = (Icon: LucideIcon, accent = false) => (
    <span
      className={cn(
        'flex size-7 shrink-0 items-center justify-center rounded-lg border',
        accent
          ? 'border-primary/30 bg-primary/10 text-primary-text'
          : 'border-border/70 bg-muted/40 text-muted-foreground',
      )}
    >
      <Icon className="size-3.5" />
    </span>
  )

  function resultEntry(r: SearchResult, section: string): Entry {
    const path =
      r.type === 'goal'
        ? t(`goals.cat.${r.category?.toLowerCase()}`, { defaultValue: r.category })
        : r.type === 'event'
          ? t('events.kindEvent')
          : r.isHabit && r.recursPeriod
            ? periodLabel(t, i18n.language, r.recursPeriod, r.recursDays)
            : r.isInbox
              ? t('search.groupInbox')
              : [r.parentGoalTitle, r.parentMilestoneTitle].filter(Boolean).join(' › ')
    const meta = r.completedAt
      ? `✓ ${t('search.done')}`
      : r.startsAt || r.dueDate
        ? new Date(r.startsAt ?? r.dueDate!).toLocaleDateString(i18n.language, {
            month: 'short',
            day: 'numeric',
          })
        : r.tags?.length
          ? `#${r.tags[0].replace(/^#/, '')}`
          : ''
    return {
      key: r._id,
      section,
      run: () => openResult(r),
      render: (active) => (
        <>
          <span className="flex size-7 shrink-0 items-center justify-center">
            <span
              className="size-2 rounded-full"
              style={{ background: fill(CATEGORY_COLORS[r.category?.toLowerCase()] ?? '#6b6b6b') }}
            />
          </span>
          <span className="min-w-0 flex-1">
            <span
              className={cn(
                'block truncate text-sm font-medium',
                r.completedAt && 'text-muted-foreground line-through',
              )}
            >
              {highlight(r.title, q)}
            </span>
            {path && (
              <span className="block truncate text-[11.5px] text-muted-foreground/70">{path}</span>
            )}
          </span>
          {meta && (
            <span
              className={cn(
                'shrink-0 font-mono text-[11px]',
                r.completedAt ? 'text-primary-text' : 'text-muted-foreground/70',
              )}
            >
              {meta}
            </span>
          )}
          {enterHint(active)}
        </>
      ),
    }
  }

  function templateEntry(tpl: SearchTemplate, section: string): Entry {
    const Icon = tpl.icon ? habitIconComponent(tpl.icon) : categoryIcon(tpl.category)
    const sub = tpl.person
      ? t('templates.bp.inspiredBy', { name: tpl.person })
      : tpl.work
        ? t('templates.inspiredBy', { work: tpl.work, author: tpl.author })
        : ''
    return {
      key: `tpl-${tpl.id}`,
      section,
      run: () => {
        close()
        void navigate({ to: '/templates/$templateId', params: { templateId: tpl.id } })
      },
      render: (active) => (
        <>
          {iconTile(Icon)}
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium">{highlight(tpl.title, q)}</span>
            {sub && (
              <span className="block truncate text-[11.5px] text-muted-foreground/70">{sub}</span>
            )}
          </span>
          <span className="shrink-0 font-mono text-[11px] text-muted-foreground/70">
            {tpl.habit ? t('search.templateHabit') : t('search.templateGoal')}
          </span>
          {enterHint(active)}
        </>
      ),
    }
  }

  function simpleEntry(
    key: string,
    section: string,
    Icon: LucideIcon,
    label: string,
    run: () => void,
    opts: { accent?: boolean; kbd?: string } = {},
  ): Entry {
    return {
      key,
      section,
      run,
      render: (active) => (
        <>
          {iconTile(Icon, opts.accent)}
          <span className="min-w-0 flex-1 truncate text-sm font-medium">{label}</span>
          {opts.kbd && !isMobile && (
            <kbd className="rounded border border-border/70 bg-muted/50 px-1.5 font-mono text-[10.5px] text-muted-foreground">
              {opts.kbd}
            </kbd>
          )}
          {enterHint(active)}
        </>
      ),
    }
  }

  const entries: Entry[] = []
  if (!q) {
    const byId = new Map(allNodes.map((n) => [n._id as string, n]))
    for (const id of recentIds) {
      const n = byId.get(id)
      if (n && entries.length < 5) entries.push(resultEntry(n, t('search.recent')))
    }
    const go = (to: string) => () => {
      close()
      void navigate({ to } as never)
    }
    const pages: [string, LucideIcon, string][] = [
      ['today', CheckCircle2, '/today'],
      ['inbox', Inbox, '/everyday'],
      ['discover', Compass, '/explore'],
      ['stats', BarChart3, '/stats'],
      ['leaderboard', Trophy, '/leaderboard'],
      ['settings', Settings, '/settings'],
    ]
    for (const [key, Icon, to] of pages)
      entries.push(simpleEntry(`page-${key}`, t('search.jumpTo'), Icon, t(`nav.${key}`), go(to)))
    const actions = t('search.actions')
    entries.push(
      simpleEntry(
        'new-task',
        actions,
        Plus,
        t('search.newTask'),
        () => {
          close()
          if (window.location.pathname.startsWith('/everyday'))
            window.dispatchEvent(new CustomEvent('inbox-compose', { detail: { tab: 'tasks' } }))
          else void navigate({ to: '/everyday', search: { addTask: true, tab: 'tasks' } })
        },
        { accent: true, kbd: 'T' },
      ),
      simpleEntry(
        'new-habit',
        actions,
        Flame,
        t('search.newHabit'),
        () => {
          close()
          if (window.location.pathname.startsWith('/everyday'))
            window.dispatchEvent(new CustomEvent('inbox-compose', { detail: { tab: 'habits' } }))
          else void navigate({ to: '/everyday', search: { addTask: true, tab: 'habits' } })
        },
        { accent: true, kbd: 'H' },
      ),
      simpleEntry(
        'new-goal',
        actions,
        Target,
        t('search.newGoal'),
        () => {
          close()
          void navigate({ to: '/goals/new', search: { goal: undefined } })
        },
        { accent: true, kbd: 'G' },
      ),
      simpleEntry(
        'brain-dump',
        actions,
        Mic,
        t('search.brainDump'),
        () => {
          close()
          openCapture()
        },
        { accent: true, kbd: '⌘B' },
      ),
      simpleEntry(
        'ask-coach',
        actions,
        MessageCircle,
        t('search.askCoach', { coach: coachName }),
        () => askCoach(),
        { accent: true },
      ),
      simpleEntry('review', actions, ClipboardList, t('search.weeklyReview'), go('/review'), {
        accent: true,
      }),
    )
  } else {
    for (const g of GROUP_ORDER) {
      const items = results.filter((r) => groupOf(r) === g)
      const label = t(`search.group${g[0].toUpperCase()}${g.slice(1)}`)
      for (const r of items) entries.push(resultEntry(r, `${label} · ${items.length}`))
    }
    if (templateHits.length) {
      const label = `${t('search.groupTemplates')} · ${templateHits.length}`
      for (const tpl of templateHits) entries.push(templateEntry(tpl, label))
    }
    const actions = t('search.actions')
    entries.push(
      simpleEntry(
        'add-inbox',
        actions,
        Plus,
        t('search.addInbox', { title: q }),
        () => void addToInbox('task'),
        { accent: true },
      ),
      simpleEntry(
        'add-habit',
        actions,
        RefreshCw,
        t('search.addHabit', { title: q }),
        () => void addToInbox('habit'),
        { accent: true },
      ),
      simpleEntry(
        'ask',
        actions,
        MessageCircle,
        t('search.askAbout', { coach: coachName, title: q }),
        () => askCoach(q),
        { accent: true, kbd: '⌘↵' },
      ),
    )
  }

  if (coaching)
    for (let i = entries.length - 1; i >= 0; i--)
      if (PERSONAL_ENTRIES.has(entries[i].key)) entries.splice(i, 1)

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIdx((i) => Math.min(i + 1, entries.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIdx((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && q && !coaching) {
      e.preventDefault()
      askCoach(q)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      entries[activeIdx]?.run()
    } else if (e.key === 'Escape') {
      close()
    }
  }

  useEffect(() => {
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' })
  }, [activeIdx])

  if (!open) return null

  const filters: Filter[] = ['all', 'goals', 'tasks', 'habits', 'inbox', 'templates']
  let lastSection = ''

  const panel = (
    <div
      className={cn(
        'flex flex-col overflow-hidden bg-background',
        isMobile
          ? 'h-full w-full pt-[max(env(safe-area-inset-top),8px)]'
          : 'mx-4 max-h-[72vh] w-full max-w-xl rounded-2xl border border-border/60 shadow-2xl',
      )}
      onClick={(e) => e.stopPropagation()}
    >
      {isMobile && (
        <div className="mx-auto mt-1 mb-2 h-1 w-9 rounded-full bg-muted-foreground/30" />
      )}
      <div
        className={cn(
          'flex items-center gap-3 px-4',
          isMobile
            ? 'mx-3 rounded-2xl border border-border/70 bg-muted/30 py-1'
            : 'border-b border-border/60',
        )}
      >
        <Search className="size-4 shrink-0 text-muted-foreground" />
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t('search.placeholderJump')}
          className="h-11 min-w-0 flex-1 bg-transparent text-[15px] text-foreground outline-none placeholder:text-muted-foreground/60"
        />
        {isMobile ? (
          <button
            type="button"
            onClick={close}
            className="cursor-pointer text-sm font-semibold text-primary-text"
          >
            {t('common.cancel')}
          </button>
        ) : (
          <kbd className="rounded border border-border/60 bg-muted/50 px-1.5 font-mono text-[10.5px] text-muted-foreground">
            esc
          </kbd>
        )}
      </div>

      {q && (
        <div className="flex flex-wrap gap-1.5 px-4 pt-2.5">
          {filters.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={cn(
                'cursor-pointer rounded-full border px-2.5 py-0.5 text-[11.5px] font-semibold transition-colors',
                filter === f
                  ? 'border-foreground bg-foreground text-background'
                  : 'border-border/70 text-muted-foreground hover:text-foreground',
              )}
            >
              {f === 'all' ? t('search.all') : t(`search.group${f[0].toUpperCase()}${f.slice(1)}`)}
            </button>
          ))}
        </div>
      )}

      <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto p-1.5">
        {noCache && (
          <p className="flex items-center gap-2 px-3 pt-3 pb-1 text-sm text-muted-foreground">
            <WifiOff className="size-3.5 shrink-0" />
            {t('search.offlineEmpty')}
          </p>
        )}
        {q && results.length === 0 && templateHits.length === 0 && !noCache && (
          <p className="px-3 pt-3 pb-1 text-sm text-muted-foreground">{t('search.tryOther')}</p>
        )}
        {entries.map((entry, i) => {
          const header = entry.section !== lastSection
          lastSection = entry.section
          const active = i === activeIdx
          return (
            <div key={entry.key}>
              {header && (
                <p className="px-2.5 pt-3 pb-1 text-[10px] font-extrabold uppercase tracking-[0.14em] text-muted-foreground/60">
                  {entry.section}
                </p>
              )}
              <button
                type="button"
                data-active={active}
                onClick={entry.run}
                onMouseEnter={() => !isMobile && setActiveIdx(i)}
                className={rowCls(active)}
              >
                {entry.render(active)}
              </button>
            </div>
          )
        })}
      </div>

      {!isMobile && (
        <div className="flex items-center gap-4 border-t border-border/60 px-4 py-2 text-[10.5px] text-muted-foreground/70">
          <span>↑↓ {t('search.navigate')}</span>
          <span>↵ {t('search.open')}</span>
          {q && !coaching && <span>⌘↵ {t('search.askHint', { coach: coachName })}</span>}
          <span className="ml-auto">esc {t('search.close')}</span>
        </div>
      )}
    </div>
  )

  return (
    <div
      className={cn(
        'fixed inset-0 z-[60] flex justify-center',
        isMobile ? 'bg-background' : 'items-start bg-black/60 pt-[14vh] backdrop-blur-sm',
      )}
      onClick={close}
    >
      {panel}
    </div>
  )
}
